import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

const AdminCardAssignmentModal = ({ isOpen, onClose }) => {
  const [userType, setUserType] = useState('teacher');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableAdminCards, setAvailableAdminCards] = useState([]);
  const [assignedAdminCards, setAssignedAdminCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [cardSearch, setCardSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAdminCardData();
      loadUsers();
    }
  }, [isOpen, userType]);

  useEffect(() => {
    if (selectedUser) {
      loadAssignedCards();
    }
  }, [selectedUser]);

  const loadAdminCardData = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.TEACHERS}/admin-cards/available`);
      if (response.ok) {
        const data = await response.json();
        setAvailableAdminCards(data.adminCards);
      }
    } catch (error) {
      console.error('Error loading admin cards:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const endpoint = userType === 'teacher' ? API_ENDPOINTS.TEACHERS : API_ENDPOINTS.STAFF;

      const response = await fetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadAssignedCards = async () => {
    if (!selectedUser) return;

    try {
      const endpoint = userType === 'teacher'
        ? `${API_ENDPOINTS.TEACHERS}/${selectedUser.id}/admin-cards`
        : `${API_ENDPOINTS.STAFF}/${selectedUser.id}/admin-cards`;

      const response = await fetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        setAssignedAdminCards(data.assignedAdminCards || []);
      }
    } catch (error) {
      console.error('Error loading assigned cards:', error);
    }
  };

  const handleAssignCard = async (cardId) => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      const endpoint = userType === 'teacher'
        ? `${API_ENDPOINTS.TEACHERS}/${selectedUser.id}/assign-admin-cards`
        : `${API_ENDPOINTS.STAFF}/${selectedUser.id}/assign-admin-cards`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminCards: [cardId] })
      });

      if (response.ok) {
        loadAssignedCards();
        alert('Admin card assigned successfully!');
      } else {
        alert('Failed to assign admin card');
      }
    } catch (error) {
      console.error('Error assigning card:', error);
      alert('Failed to assign admin card');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignCard = async (cardId) => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      const endpoint = userType === 'teacher'
        ? `${API_ENDPOINTS.TEACHERS}/${selectedUser.id}/unassign-admin-cards`
        : `${API_ENDPOINTS.STAFF}/${selectedUser.id}/unassign-admin-cards`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminCards: [cardId] })
      });

      if (response.ok) {
        loadAssignedCards();
        alert('Admin card unassigned successfully!');
      } else {
        alert('Failed to unassign admin card');
      }
    } catch (error) {
      console.error('Error unassigning card:', error);
      alert('Failed to unassign admin card');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filteredUsers = users.filter(user =>
    `${user.firstName} ${user.lastName} ${user.employeeId || ''} ${user.email || ''}`.toLowerCase().includes(userSearch.toLowerCase())
  );
  const filteredCards = availableAdminCards.filter(card =>
    `${card.title} ${card.description} ${card.category}`.toLowerCase().includes(cardSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Admin Card Assignment</h2>
              <p className="text-sm text-gray-600 mt-1">Grant only the dashboard tools each teacher or staff member needs.</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm font-semibold text-blue-700">Users loaded</p>
              <p className="text-2xl font-black text-blue-950">{users.length}</p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-semibold text-green-700">Assigned cards</p>
              <p className="text-2xl font-black text-green-950">{assignedAdminCards.length}</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-700">Available cards</p>
              <p className="text-2xl font-black text-amber-950">{availableAdminCards.length}</p>
            </div>
          </div>

          {/* User Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select User Type
            </label>
            <select
              value={userType}
              onChange={(e) => {
                setUserType(e.target.value);
                setSelectedUser(null);
                setAssignedAdminCards([]);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="teacher">Teachers</option>
              <option value="staff">Staff Members</option>
            </select>
          </div>

          {/* User Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select {userType === 'teacher' ? 'Teacher' : 'Staff Member'}
            </label>
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search by name, email, or employee ID"
              className="border border-gray-300 rounded-md px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
            <select
              value={selectedUser?.id || ''}
              onChange={(e) => {
                const user = users.find(u => u.id === e.target.value);
                setSelectedUser(user);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            >
              <option value="">Choose a {userType}...</option>
              {filteredUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} - {user.employeeId}
                </option>
              ))}
            </select>
          </div>

          {selectedUser && (
            <div className="space-y-6">
              {/* Selected User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-2">Selected User</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Name:</span> {selectedUser.firstName} {selectedUser.lastName}
                  </div>
                  <div>
                    <span className="font-medium">ID:</span> {selectedUser.employeeId}
                  </div>
                  <div>
                    <span className="font-medium">Email:</span> {selectedUser.email}
                  </div>
                </div>
              </div>

              {/* Admin Cards Management */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Available Cards */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-4">Available Admin Cards</h3>
                  <input
                    value={cardSearch}
                    onChange={(e) => setCardSearch(e.target.value)}
                    placeholder="Search cards or category"
                    className="border border-gray-300 rounded-md px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {filteredCards.map(card => {
                      const isAssigned = assignedAdminCards.includes(card.id);
                      return (
                        <div
                          key={card.id}
                          className={`border rounded-lg p-3 ${isAssigned ? 'bg-gray-100' : 'bg-white'}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-2xl">{card.icon}</span>
                                <h4 className="font-medium text-gray-800">{card.title}</h4>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{card.description}</p>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                card.category === 'academic' ? 'bg-blue-100 text-blue-800' :
                                card.category === 'finance' ? 'bg-green-100 text-green-800' :
                                card.category === 'hr' ? 'bg-purple-100 text-purple-800' :
                                card.category === 'communication' ? 'bg-orange-100 text-orange-800' :
                                card.category === 'monitoring' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {card.category.toUpperCase()}
                              </span>
                            </div>
                            <button
                              onClick={() => isAssigned ? handleUnassignCard(card.id) : handleAssignCard(card.id)}
                              disabled={loading}
                              className={`px-3 py-1 rounded text-sm font-medium ${
                                isAssigned
                                  ? 'bg-red-600 text-white hover:bg-red-700'
                                  : 'bg-green-600 text-white hover:bg-green-700'
                              } disabled:opacity-50`}
                            >
                              {isAssigned ? 'Remove' : 'Assign'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Assigned Cards Summary */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-4">
                    Assigned Cards ({assignedAdminCards.length})
                  </h3>
                  {assignedAdminCards.length > 0 ? (
                    <div className="space-y-3">
                      {assignedAdminCards.map(cardId => {
                        const card = availableAdminCards.find(c => c.id === cardId);
                        if (!card) return null;

                        return (
                          <div key={cardId} className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center space-x-3">
                              <span className="text-xl">{card.icon}</span>
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-800">{card.title}</h4>
                                <p className="text-sm text-gray-600">{card.description}</p>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                card.category === 'academic' ? 'bg-blue-100 text-blue-800' :
                                card.category === 'finance' ? 'bg-green-100 text-green-800' :
                                card.category === 'hr' ? 'bg-purple-100 text-purple-800' :
                                card.category === 'communication' ? 'bg-orange-100 text-orange-800' :
                                card.category === 'monitoring' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {card.category.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Admin Cards Assigned</h3>
                      <p className="text-gray-500">
                        Select cards from the left panel to assign administrative functions to this user.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCardAssignmentModal;



